<script setup lang="ts">
import {
    ref,
    reactive,
    onMounted,
    nextTick,
    computed,
    watch,
    onUnmounted,
} from 'vue'
import MobileUgcDetailItemInfo from '~/components/mobile/MobileUgcDetailItemInfo.vue'
import { ElMessage } from 'element-plus'
import { useI18n } from '~/composables/useI18n'
import {
    getInspirationListApi,
    changeUserLikeStateApi,
} from '~/api/inspiration'
import {
    getInspirationType,
    formatPath,
    getInspirationOutputType,
} from '~/utils/inspiration'
import { getResourceThumbnail } from '~/utils/common'
import { emitter, showLoginModal } from '~/utils/emitter'
import { useMusicPlayerStore } from '~/stores/musicPlayer'
import { useUserStore } from '~/stores/user'
import { prepareAllowedInspirationNavigationItem } from '~/utils/compliance-models'

const { t } = useI18n()
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const musicPlayer = useMusicPlayerStore()
const getInspirationId = (item: any) => item?.ins_id || item?.id

// ==================== 音乐类型支持 ====================
// 判断是否是音乐灵感类型
const isMusicType = computed(
    () => props.inspirationType === 'MUSIC_CREATION_GENERATION'
)

// 获取音乐类型标签
const getMusicTypeLabel = (item: any) => {
    const type = item.param?.type
    if (type === 'VOCAL_SONG') {
        return t('menus.aiMusicPage.vocal')
    } else if (type === 'ONLY_MUSIC') {
        return t('menus.aiMusicPage.instMusic')
    }
    return t('menus.aiMusicPage.vocal')
}

// 格式化点赞数
const formatLikeCount = (count: number | undefined) => {
    if (!count) return '0'
    if (count >= 10000) {
        return (count / 10000).toFixed(1) + 'w'
    }
    if (count >= 1000) {
        return (count / 1000).toFixed(1) + 'k'
    }
    return count.toString()
}

// 播放/暂停音频
const togglePlay = (item: any) => {
    const itemId = getInspirationId(item)
    const audioUrl = item.output_audio || item.output
    if (!audioUrl) {
        ElMessage.warning(t('inspiration.noAudio') || '暂无音频')
        return
    }

    // 使用全局播放器播放
    musicPlayer.play(itemId, audioUrl)
}

// 音乐做同款
const handleMusicMakeSimilar = (item: any) => {
    // 通过事件总线发送数据，填充表单
    emitter.emit('fill-music-form', item)

    // 如果不在 text-to-music 页面，则跳转
    if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname
        if (!currentPath.includes('text-to-music')) {
            const timestamp = Date.now()
            localStorage.setItem('inspirationData', JSON.stringify(item))
            navigateTo(`/text-to-music?customType=inspiration&t=${timestamp}`)
        }
    }
}

// 打开音乐详情弹窗（转换数据格式以适配 UgcDetailItemInfo 组件）
const openMusicMask = (item: any) => {
    showMask.value = true
    // 转换数据格式：API 返回的 param.lyrics 需要映射到 music.generated_lyrics
    currentDetailItem.value = {
        ...item,
        music: {
            music_title: item.music?.music_title || '', // 使用 prompt 作为标题
            generated_lyrics: item.music?.generated_lyrics || '', // 歌词从 param.lyrics 获取
            music_style: item.param?.styles?.join(', ') || '',
        },
    }
}
const tempData = ref<any[]>([]) // 初始为空，等待异步加载 - 瀑布流数据容器
const resData = ref<any[]>([]) // 响应数据
const total_page = ref(0) // 总页数
const total_inspiration_data = ref(-1) //总数据量

// 仅用于详情弹窗切换：移动端用 MobileUgcDetailItemInfo，PC 继续沿用 tool-ugc-detail-item-info
const isMobile = ref(false)
const updateIsMobile = () => {
    if (typeof window === 'undefined') return
    isMobile.value = window.innerWidth < 768
}

// ===== 图片渐进加载（thumbnail -> origin，保证不闪）=====
const inspirationImageLoadedState = reactive<Record<string, boolean>>({})

const getInspirationImageKey = (item: any) => {
    return `${String(getInspirationId(item) || '')}|${String(item?.output || '')}`
}

const isInspirationImageLoaded = (item: any) => {
    const key = getInspirationImageKey(item)
    return key ? !!inspirationImageLoadedState[key] : false
}

const markInspirationImageLoaded = (item: any) => {
    const key = getInspirationImageKey(item)
    if (!key) return
    inspirationImageLoadedState[key] = true
}

const markInspirationImageError = (item: any) => {
    const key = getInspirationImageKey(item)
    if (!key) return
    // 失败保持 thumbnail
    inspirationImageLoadedState[key] = false
}
// 显示类型
const props = defineProps({
    // 判断是遮罩层还是卡片显示，动态调整更多按钮
    showType: {
        type: String,
        default: '',
    },
    category: {
        type: String,
        default: '',
    },
    inspirationType: {
        type: String,
        default: '',
    },
    types: {
        type: Array<string>,
        default: [],
    },
})

// 瀑布流配置
const config = {
    baseColumnWidth: 240, // 基准列宽
    maxColumnWidth: 300, // 最大列宽
    minColumnWidth: 180, // 最小列宽
    gap: 16,
    minColumns: 1,
    defaultAspectRatio: 3 / 4,
}

// 容器宽度为 0（不可见）时会导致误判为单列，这里用一个很小的阈值判断“可布局”
const MIN_VISIBLE_WIDTH = 50

// 容器引用
const containerRef = ref<HTMLElement | null>(null)

// 瀑布流状态
const waterfall = ref({
    columns: 2,
    columnWidth: config.baseColumnWidth, // 动态列宽
    columnHeights: [] as number[],
    items: [] as any[],
})

// 计算列数和列宽
const calculateColumnsAndWidth = () => {
    if (!containerRef.value)
        return {
            columns: config.minColumns,
            columnWidth: config.baseColumnWidth,
        }

    const containerWidth = containerRef.value.offsetWidth

    /**
     * 小红书式体验：移动端尽量 2 列（通过降低基准/最小列宽实现）
     * - 只影响本组件，不影响其它瀑布流/组件
     * - 以“容器实际宽度”为准（比 window.innerWidth 更可靠）
     */
    const getResponsiveColConfig = (w: number) => {
        if (w <= 360) return { base: 150, min: 140 } // 超小屏
        if (w <= 420) return { base: 180, min: 160 } // iPhone SE / 小屏手机
        if (w <= 768) return { base: 220, min: 170 } // 平板/小屏桌面
        return {
            base: config.baseColumnWidth,
            min: config.minColumnWidth,
        } // 桌面保持原逻辑
    }

    const { base, min } = getResponsiveColConfig(containerWidth)

    // 先按基准列宽计算列数
    let columns = Math.floor(
        (containerWidth + config.gap) / (base + config.gap)
    )
    columns = Math.max(columns, config.minColumns)

    // 根据列数反推最佳列宽（让内容填满容器）
    // 公式：列宽 = (容器宽度 - (列数-1) × 间距) / 列数
    let columnWidth = (containerWidth - (columns - 1) * config.gap) / columns

    // 限制列宽范围
    columnWidth = Math.max(min, Math.min(config.maxColumnWidth, columnWidth))

    return { columns, columnWidth }
}

// 初始化列高度数组
const initColumnHeights = (columnCount: number) => {
    waterfall.value.columnHeights = new Array(columnCount).fill(0)
}

// 等待容器宽度可用后再进行布局，避免不可见时宽度为 0 造成单列闪烁
const waitForContainerReady = async () => {
    if (!containerRef.value) return
    await new Promise<void>(resolve => {
        const check = () => {
            const width = containerRef.value?.offsetWidth || 0
            if (width > MIN_VISIBLE_WIDTH) {
                resolve()
            } else {
                requestAnimationFrame(check)
            }
        }
        check()
    })
}

// 获取最短列的索引
const getShortestColumnIndex = () => {
    const minHeight = Math.min(...waterfall.value.columnHeights)
    return waterfall.value.columnHeights.indexOf(minHeight)
}

// 计算元素的高度（基于动态列宽和宽高比）
const calculateItemHeight = (item: any) => {
    let aspectRatio = config.defaultAspectRatio
    let source = 'default'

    if (item.param?.aspect_ratio) {
        // 验证 aspect_ratio 格式是否正确（应包含 ':' 且能解析为数字）
        const ratioStr = item.param.aspect_ratio
        if (ratioStr && ratioStr.includes(':')) {
            const parts = ratioStr.split(':')
            const width = Number(parts[0])
            const height = Number(parts[1])

            // 确保解析成功且都是有效数字
            if (!isNaN(width) && !isNaN(height) && width > 0 && height > 0) {
                aspectRatio = width / height
                source = 'param.aspect_ratio'
            } else {
                console.warn(
                    `⚠️ 无效的宽高比格式: "${ratioStr}", 使用默认值 ${config.defaultAspectRatio}`
                )
            }
        } else {
            console.warn(
                `⚠️ aspect_ratio 不是宽高比格式: "${ratioStr}", 使用默认值 ${config.defaultAspectRatio}`
            )
        }
    }

    // 使用动态列宽计算实际高度
    const height = Math.ceil(waterfall.value.columnWidth / aspectRatio)

    // 只在第一个元素打印详细信息
    // if (waterfall.value.items.length === 0) {
    //   console.log('📏 计算第一个元素高度:', {
    //     宽高比来源: source,
    //     宽高比: aspectRatio.toFixed(2),
    //     列宽: waterfall.value.columnWidth,
    //     计算高度: height,
    //     原始数据: item.param?.aspect_ratio
    //   })
    // }

    return height
}

// 布局单个元素
const layoutItem = (item: any, index: number) => {
    const columnIndex = getShortestColumnIndex()
    // 计算元素高度
    const itemHeight = calculateItemHeight(item)
    // 计算元素位置（使用动态列宽）
    const left = columnIndex * (waterfall.value.columnWidth + config.gap)
    const top = waterfall.value.columnHeights[columnIndex]

    // 创建瀑布流元素
    const waterfallItem = {
        ...item,
        style: {
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            width: `${waterfall.value.columnWidth}px`, // 使用动态列宽
            height: `${itemHeight}px`,
            // 图片懒加载后是否需要重新计算高度
            reCaculateSize: false,
            originIndex: index,
        },
    }
    // 更新该列的高度（验证 itemHeight 是有效数字）
    if (!isNaN(itemHeight) && itemHeight > 0) {
        waterfall.value.columnHeights[columnIndex]! += itemHeight + config.gap
    } else {
        console.error('❌ 计算的元素高度无效:', { item, itemHeight })
    }
    return waterfallItem
}

// 执行瀑布流布局
const layout = () => {
    if (!containerRef.value) {
        console.warn('⚠️ 布局中止：containerRef 未挂载')
        return
    }

    // 容器不可见时宽度为 0，直接跳过，等待可见后再布局
    if ((containerRef.value.offsetWidth || 0) <= MIN_VISIBLE_WIDTH) {
        return
    }

    // 计算列数和动态列宽
    const { columns, columnWidth } = calculateColumnsAndWidth()

    // 如果列数或列宽变化，更新状态
    if (
        columns !== waterfall.value.columns ||
        columnWidth !== waterfall.value.columnWidth
    ) {
        waterfall.value.columns = columns
        waterfall.value.columnWidth = columnWidth
    }

    // 初始化，清空之前的布局
    initColumnHeights(columns)

    // 清空旧的瀑布流布局项，避免重复
    waterfall.value.items = []
    tempData.value.forEach((item, index) => {
        const waterfallItem = layoutItem(item, index)
        waterfall.value.items.push(waterfallItem)
    })
}

// 视频或图片加载完成时重新计算高度
const onMediaLoad = (event: Event, item: any) => {
    const target = event.target as HTMLImageElement | HTMLVideoElement

    if (!target) return

    const actualWidth =
        (target as HTMLVideoElement)?.videoWidth ||
        (target as HTMLImageElement).naturalWidth ||
        (target as HTMLImageElement).offsetWidth
    const actualHeight =
        (target as HTMLVideoElement)?.videoHeight ||
        (target as HTMLImageElement).naturalHeight ||
        (target as HTMLImageElement).offsetHeight

    if (actualWidth && actualHeight) {
        const newAspectRatio = actualWidth / actualHeight
        const oldHeight = parseFloat(item.style.height)
        const newHeight = Math.ceil(
            waterfall.value.columnWidth / newAspectRatio
        ) // 使用动态列宽

        if (Math.abs(newHeight - oldHeight) > 1) {
            // 高度变化超过1px，触发重新布局
            layout()
        }
    }
}

// 图片加载事件（thumbnail/origin）
const handleInspirationThumbLoad = (event: Event, item: any) => {
    onMediaLoad(event, item)
}

const handleInspirationOriginLoad = (event: Event, item: any) => {
    markInspirationImageLoaded(item)
    onMediaLoad(event, item)
}

const handleInspirationOriginError = (_event: Event, item: any) => {
    markInspirationImageError(item)
}

// 记录缩略图是否加载失败（避免显示 broken image 图标）
const inspirationThumbErrorState = reactive<Record<string, boolean>>({})

const markInspirationThumbError = (item: any) => {
    const key = getInspirationImageKey(item)
    if (!key) return
    inspirationThumbErrorState[key] = true
}

const isInspirationThumbError = (item: any) => {
    const key = getInspirationImageKey(item)
    return key ? !!inspirationThumbErrorState[key] : false
}

// 节流 + 防抖优化的布局函数
let layoutTimer: ReturnType<typeof setTimeout> | null = null
let lastLayoutTime = 0
const THROTTLE_DELAY = 150 // 节流：最多 150ms 执行一次，保证实时反馈
const DEBOUNCE_DELAY = 300 // 防抖：停止调整后 300ms 执行最后一次，确保最终状态正确

const optimizedLayout = () => {
    const now = Date.now()

    // 清除之前的防抖定时器
    if (layoutTimer) {
        clearTimeout(layoutTimer)
    }

    // 如果距离上次执行超过节流时间，立即执行（提供实时反馈）
    if (now - lastLayoutTime >= THROTTLE_DELAY) {
        lastLayoutTime = now
        layout()
    }

    // 设置防抖，确保停止调整后执行最后一次（确保最终状态准确）
    layoutTimer = setTimeout(() => {
        lastLayoutTime = Date.now()
        layout()
    }, DEBOUNCE_DELAY)
}

// 监听窗口大小变化
let resizeObserver: ResizeObserver | null = null

// DevTools 切换设备时，window.resize 有时触发时机偏早；用 rAF 等一帧拿到最新的容器宽度
const onViewportResize = () => {
    requestAnimationFrame(() => {
        optimizedLayout()
    })
}

const setupResizeObserver = () => {
    if (!containerRef.value) return

    // 避免重复绑定（initData/切换类型会多次进入）
    if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
    }

    resizeObserver = new ResizeObserver(() => {
        // 容器不可见时 offsetWidth 可能为 0，跳过以避免单列闪烁
        if ((containerRef.value?.offsetWidth || 0) > MIN_VISIBLE_WIDTH) {
            optimizedLayout() // 使用优化后的布局函数
        }
    })
    resizeObserver.observe(containerRef.value)
}

onMounted(async () => {
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    // 动态导入并注册 ldrs 组件
    try {
        const { dotSpinner } = await import('ldrs')
        dotSpinner.register()
        // 确保注册完成后再设置 isClient，避免组件在注册前渲染
        isClient.value = true
    } catch (error) {
        console.error('Failed to load ldrs:', error)
        // 即使加载失败也设置为 true，避免无限等待
        isClient.value = true
    }

    // 初始化加载数据（await 确保数据加载完成后再设置滚动监听）
    await initData()

    // 监听视口变化（DevTools 切换设备/旋转/缩放）
    window.addEventListener('resize', onViewportResize)
    window.visualViewport?.addEventListener('resize', onViewportResize)
})

onUnmounted(() => {
    // 停止音乐播放
    musicPlayer.stop()

    window.removeEventListener('resize', updateIsMobile)
    // 清理监听器和定时器
    if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
    }
    if (layoutTimer) {
        clearTimeout(layoutTimer)
    }

    // 移除滚动监听
    if (scrollContainer.value) {
        scrollContainer.value.removeEventListener('scroll', throttledScroll)
    }
    if (alsoListenWindow.value) {
        window.removeEventListener('scroll', throttledScroll)
    }

    if (scrollTimer) {
        clearTimeout(scrollTimer)
    }

    window.removeEventListener('resize', onViewportResize)
    window.visualViewport?.removeEventListener('resize', onViewportResize)

    // 恢复 body 滚动
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
})

// 计算容器总高度（取最高列的高度）
const containerHeight = computed(() => {
    // 过滤掉无效值（NaN、undefined、null）
    const validHeights = waterfall.value.columnHeights.filter(
        h => typeof h === 'number' && !isNaN(h)
    )
    const height = validHeights.length > 0 ? Math.max(...validHeights) : 0

    return height
})

// 视频 hover 播放控制（避免把 enter/leave 绑在 <video> 上导致 overlay 抖动）
const warmedVideos = new WeakSet<HTMLVideoElement>()
const pauseTimers = new WeakMap<HTMLVideoElement, ReturnType<typeof setTimeout>>()

const isVideoItem = (item: any) =>
    getInspirationType(item.type) === 'VIDEO' ||
    getInspirationOutputType(item.output) === 'VIDEO'

const warmUpVideoOnce = (video: HTMLVideoElement) => {
    if (warmedVideos.has(video)) return
    warmedVideos.add(video)
    try {
        // 只提高 preload 等级，不主动调用 load()：
        // load() 可能导致 poster/首帧渲染管线重置，从而在“第一次 hover 播放”时出现闪一下。
        video.preload = 'auto'
    } catch {
        // ignore
    }
}

const playVideoForItem = async (event: MouseEvent, item: any) => {
    if (!isVideoItem(item)) return
    const host = event.currentTarget as HTMLElement | null
    const video = host?.querySelector('video.media-content') as
        | HTMLVideoElement
        | null
    if (!video) return

    const t = pauseTimers.get(video)
    if (t) clearTimeout(t)
    pauseTimers.delete(video)

    warmUpVideoOnce(video)
    if (!video.paused) return
    try {
        await video.play()
    } catch (error: any) {
        if (error?.name !== 'AbortError') {
            console.error('视频播放失败:', error)
        }
    }
}

const pauseVideoForItem = (event: MouseEvent, item: any) => {
    if (!isVideoItem(item)) return
    const host = event.currentTarget as HTMLElement | null
    const video = host?.querySelector('video.media-content') as
        | HTMLVideoElement
        | null
    if (!video) return

    const prev = pauseTimers.get(video)
    if (prev) clearTimeout(prev)
    // 延迟 pause，避免轻微移出/移入导致反复 Abort + 206
    const timer = setTimeout(() => {
        pauseTimers.delete(video)
        if (!video.paused) video.pause()
    }, 250)
    pauseTimers.set(video, timer)
}

// ===== 封装的异步请求函数 =====
const fetchInspirationData = async (page: number = 1) => {
    const params = {
        page: page,
        size: 20,
        type: props.inspirationType,
        types: props.types,
    }

    const res = await getInspirationListApi(params)
    // 容错：过滤掉缺少关键字段导致渲染异常的记录
    const rawList = res?.data?.content || []
    const normalizedList = rawList.map((item: any) => ({
        ...item,
        ins_id: item?.ins_id || item?.id,
    }))
    resData.value = normalizedList.filter((item: any) => {
        const hasOutput =
            item && typeof item.output === 'string' && item.output.trim()
        const hasId = item && getInspirationId(item)
        if (!hasOutput || !hasId) {
            console.warn('跳过无效灵感数据', { item })
            return false
        }
        return true
    })
    total_page.value = res?.data?.total_page || 1
    total_inspiration_data.value = res?.data?.total || 0
}

// 重新加载数据
watch(
    () => props.inspirationType,
    async newV => {
        currentPage.value = 1
        tempData.value = [] // 清空旧数据
        // 使用 await 确保数据加载完成后滚动监听被正确设置
        await initData()
    }
)

// ===== 初始化：加载第一页数据 =====
const initData = async () => {
    initLoading.value = true
    hasMore.value = true

    try {
        await fetchInspirationData(currentPage.value)
        tempData.value = resData.value
    } catch (error) {
        console.error('❌ 加载失败:', error)
    } finally {
        initLoading.value = false

        // 检查是否还有更多数据
        if (currentPage.value >= total_page.value) {
            hasMore.value = false
        }

        // 数据加载完成后，等待 DOM 更新再执行布局
        // 必须在 initLoading=false 之后执行，因为此时瀑布流容器才会渲染
        if (tempData.value.length > 0) {
            await nextTick()
            await waitForContainerReady()
            setupResizeObserver()
            layout()

            // 瀑布流容器渲染后，重新设置滚动监听
            setupScrollListener()
        }
    }
}

// 遮罩层控制
const showMask = ref(false)
const maskRef = ref<HTMLElement | null>(null)

// 当前遮罩层信息
const currentDetailItem = ref()
// 打开遮罩层
const openMask = (item: any) => {
    showMask.value = true
    currentDetailItem.value = item
}

// 监听遮罩层状态，控制 body 滚动（SSR 保护）
watch(
    showMask,
    newVal => {
        if (typeof window === 'undefined' || typeof document === 'undefined')
            return

        if (newVal) {
            // 打开遮罩层，禁止 body 滚动
            const scrollbarWidth =
                window.innerWidth - document.documentElement.clientWidth
            document.body.style.overflow = 'hidden'
            document.body.style.paddingRight = scrollbarWidth + 'px'
        } else {
            // 关闭遮罩层，恢复 body 滚动
            document.body.style.overflow = ''
            document.body.style.paddingRight = ''
        }
    },
    { immediate: true }
)

// 点赞：按作品 ID 防止并发/连点导致状态抖动
const likePendingIds = new Set<string | number>()

const updateLikeFields = (obj: any, hasLike: boolean, likesNum: number) => {
    if (!obj) return
    obj.has_like = hasLike
    obj.likes_num = likesNum
}

const changeLikeStateForItem = async (event: MouseEvent, item: any) => {
    // 检查登录状态
    if (!isLoggedIn.value) {
        showLoginModal()
        return
    }

    const inspirationId = getInspirationId(item)
    if (!inspirationId) return

    // 防止重复点击造成点赞数抖动/并发请求
    if (likePendingIds.has(inspirationId)) return
    likePendingIds.add(inspirationId)

    event?.preventDefault?.()

    // 先做乐观更新：立刻反馈 UI
    const prevHasLike = !!item?.has_like
    const prevLikes = item?.likes_num ?? 0
    const nextHasLike = !prevHasLike
    const nextLikes = nextHasLike ? prevLikes + 1 : Math.max(0, prevLikes - 1)

    // 同步更新：视图列表、原始数据、详情弹窗、以及触发事件的 item 引用（可能是其中之一）
    const viewItem = waterfall.value?.items?.find(
        i => getInspirationId(i) === inspirationId
    )
    const rawItem = tempData.value?.find(i => getInspirationId(i) === inspirationId)
    const detailItem =
        currentDetailItem.value &&
            getInspirationId(currentDetailItem.value) === inspirationId
            ? currentDetailItem.value
            : null

        ;[item, viewItem, rawItem, detailItem].forEach(i =>
            updateLikeFields(i, nextHasLike, nextLikes)
        )

    const params = {
        ins_id: inspirationId,
        like: nextHasLike,
    }

    try {
        const res = await changeUserLikeStateApi(params)

        // 检查接口返回状态
        if (res.status != 200) {
            // 如果状态不是200：回滚乐观更新
            ;[item, viewItem, rawItem, detailItem].forEach(i =>
                updateLikeFields(i, prevHasLike, prevLikes)
            )
            ElMessage.warning(res.message || t('inspiration.operationFailed'))
            return
        }
    } catch (error) {
        console.error('点赞失败:', error)
            // 点赞失败：回滚乐观更新
            ;[item, viewItem, rawItem, detailItem].forEach(i =>
                updateLikeFields(i, prevHasLike, prevLikes)
            )
        ElMessage.error(t('inspiration.likeFailed'))
    } finally {
        likePendingIds.delete(inspirationId)
    }
}

// 分页相关状态
const loading = ref(false)
const initLoading = ref(false)
const hasMore = ref(true)
const currentPage = ref(1)
const isClient = ref(false) // 用于检测是否在客户端

// 自动检测滚动容器（可能同时监听多个）
const scrollContainer = ref<HTMLElement | null>(null) // 找到的有 overflow-y: auto 的容器
const alsoListenWindow = ref(false) // 是否同时监听 window

// 获取滚动容器的滚动信息（检查容器和 window，返回更接近底部的那个）
const getScrollInfo = () => {
    const results: { scrollTop: number; scrollHeight: number; clientHeight: number; distanceToBottom: number }[] = []

    // 检查找到的容器
    if (scrollContainer.value) {
        const el = scrollContainer.value
        if (el.scrollHeight > el.clientHeight) {
            const distanceToBottom = el.scrollHeight - (el.scrollTop + el.clientHeight)
            results.push({
                scrollTop: el.scrollTop,
                scrollHeight: el.scrollHeight,
                clientHeight: el.clientHeight,
                distanceToBottom,
            })
        }
    }

    // 检查 window
    if (alsoListenWindow.value) {
        const scrollTop = window.scrollY || document.documentElement.scrollTop
        const scrollHeight = document.documentElement.scrollHeight
        const clientHeight = window.innerHeight
        if (scrollHeight > clientHeight) {
            const distanceToBottom = scrollHeight - (scrollTop + clientHeight)
            results.push({
                scrollTop,
                scrollHeight,
                clientHeight,
                distanceToBottom,
            })
        }
    }

    // 返回距离底部最近的那个
    if (results.length === 0) return null
    return results.reduce((a, b) => (a.distanceToBottom < b.distanceToBottom ? a : b))
}

// 检查是否应该加载更多
const checkShouldLoadMore = () => {
    if (loading.value || !hasMore.value) return

    const scrollInfo = getScrollInfo()
    if (!scrollInfo) return

    const { scrollTop, scrollHeight, clientHeight } = scrollInfo
    const distanceToBottom = scrollHeight - (scrollTop + clientHeight)

    // 距离底部小于200px时触发加载
    if (distanceToBottom < 200) {
        loadMore()
    }
}

// 加载更多数据（追加数据，显示 loading）
const loadMore = async () => {
    if (loading.value || !hasMore.value) return

    loading.value = true
    currentPage.value++

    try {
        await fetchInspirationData(currentPage.value)
        // 追加数据而不是替换，实现真正的"加载更多"
        tempData.value = [...tempData.value, ...resData.value]

        // 数据加载完成后，执行布局
        nextTick(() => {
            layout()
        })

        if (currentPage.value >= total_page.value) {
            hasMore.value = false
        }
    } catch (error) {
        console.error('加载失败:', error)
    } finally {
        loading.value = false
    }
}

// 节流处理滚动事件
let scrollTimer: ReturnType<typeof setTimeout> | null = null
const throttledScroll = () => {
    console.log('[DEBUG] 滚动事件触发')
    if (scrollTimer) return

    scrollTimer = setTimeout(() => {
        checkShouldLoadMore()
        scrollTimer = null
    }, 150)
}

// 查找最近的可滚动父容器（返回有 overflow-y: auto/scroll 的元素，如果没找到返回 null）
function findScrollContainer(element: HTMLElement | null): HTMLElement | null {
    if (!element) return null

    let parent = element.parentElement
    // 用于存储第一个"潜在"的滚动容器（有 overflow-y: auto/scroll 但内容暂时没有超出）
    let potentialContainer: HTMLElement | null = null

    while (parent) {
        const style = window.getComputedStyle(parent)
        const overflowY = style.overflowY

        // 找到第一个可滚动的父容器
        if (overflowY === 'auto' || overflowY === 'scroll') {
            // 如果内容已经超出容器高度，立即返回
            if (parent.scrollHeight > parent.clientHeight) {
                return parent
            }
            // 否则记录为潜在容器（内容加载后可能会变成真正的滚动容器）
            if (!potentialContainer) {
                potentialContainer = parent
            }
        }

        parent = parent.parentElement
    }

    // 如果没找到实际滚动的容器，但有潜在容器，返回潜在容器
    if (potentialContainer) {
        return potentialContainer
    }

    return null
}

// ===== 统一的滚动监听设置函数 =====
const setupScrollListener = () => {
    // 先移除旧的监听器
    if (scrollContainer.value) {
        scrollContainer.value.removeEventListener('scroll', throttledScroll)
    }
    if (alsoListenWindow.value) {
        window.removeEventListener('scroll', throttledScroll)
    }

    // 重新检测滚动容器
    const foundContainer = findScrollContainer(containerRef.value)
    scrollContainer.value = foundContainer

    // 策略：同时监听找到的容器和 window
    // 因为不同页面的滚动可能发生在不同的地方
    if (foundContainer) {
        foundContainer.addEventListener('scroll', throttledScroll)
    }

    // 同时监听 window（除非找到的容器已经是可滚动的）
    const containerCanScroll = foundContainer && foundContainer.scrollHeight > foundContainer.clientHeight
    alsoListenWindow.value = !containerCanScroll
    if (alsoListenWindow.value) {
        window.addEventListener('scroll', throttledScroll)
    }
}

// 导航跳转
const navigationChange = (item: any) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    const targetPath = formatPath(
        item.type,
        item.param.template_id,
        item.param.template_path
    )
    const timestamp = Date.now()
    // const targetPath = item.type
    const { item: navigationItem, defaultModel } =
        prepareAllowedInspirationNavigationItem(item)
    const defaultModelQuery = defaultModel
        ? `&defaultModel=${encodeURIComponent(defaultModel)}`
        : ''
    const path = `/${targetPath}?customType=inspiration&t=${timestamp}${defaultModelQuery}`

    const dataWriteBack = JSON.stringify(navigationItem)
    localStorage.setItem('inspirationData', dataWriteBack)
    navigateTo(path)
}

// 动态显示创建内容和其他功能
const hoveredItemId = ref(null)
</script>

<template style="position: relative">
    <div class="inspiration-root">
        <!-- 初始加载状态 -->
        <div v-if="initLoading" class="loading-state">
            <l-dot-spinner v-if="isClient" size="50" speed="0.9" color="var(--el-menu-active-color)"></l-dot-spinner>
        </div>

        <!-- ==================== 音乐类型：网格布局 + 黑胶卡片 ==================== -->
        <template v-else-if="isMusicType">
            <!-- 音乐卡片网格 - 有数据时显示 -->
            <div v-if="tempData.length > 0" ref="containerRef" class="music-grid-container">
                <div v-for="item in tempData" :key="getInspirationId(item)" class="music-card cursor-pointer"
                    @click="openMusicMask(item)">
                    <!-- 背景层：图片 + 高斯模糊 -->
                    <div class="music-card-bg" :style="{ backgroundImage: `url(${item.output})` }"></div>

                    <!-- 左上角类型标签 -->
                    <div class="music-type-tag">
                        <img src="~/assets/images/common/music-type.png"
                            srcset="~/assets/images/common/music-type.png 1x, ~/assets/images/common/music-type@2x.png 2x"
                            alt="music" aria-hidden="true" class="w-[9px] h-[9px]" width="9" height="9" />
                        <span>{{ getMusicTypeLabel(item) }}</span>
                    </div>

                    <!-- 黑胶唱片 -->
                    <div class="vinyl-record" :class="{
                        spinning: musicPlayer.isCurrentPlaying(
                            getInspirationId(item)
                        ),
                    }">
                        <div class="vinyl-outer"></div>
                        <div class="vinyl-inner">
                            <img :src="item.output" :alt="item.music?.music_title || 'Music cover'"
                                class="vinyl-cover" />
                            <div class="vinyl-center-dot"></div>
                        </div>
                    </div>

                    <!-- 常态信息区：歌曲名 + 用户信息 -->
                    <div class="info-section">
                        <!-- 歌曲名称 -->
                        <div class="song-title-row">
                            <div class="song-title">
                                {{
                                    item.music?.music_title ||
                                    item.param?.prompt ||
                                    t('inspiration.untitledMusic')
                                }}
                            </div>
                            <!-- <span class="song-type-tag">{{ getMusicTypeLabel(item) }}</span> -->
                        </div>
                        <!-- 用户信息行 -->
                        <div class="user-info-row">
                            <div class="user-left">
                                <img :src="item.user?.avatar ||
                                    'https://static.weryai.com/growth/image/9ead8a3f9b5dd9d44367d3aba6ac8a93.webp'
                                    " :alt="item.user?.user_name ||
                                        t('inspiration.unknownUser')
                                        " class="user-avatar" />
                                <span class="user-name">{{
                                    item.user?.user_name ||
                                    t('inspiration.unknownUser')
                                }}</span>
                            </div>
                            <button type="button" class="like-btn" :aria-label="item.has_like
                                ? `${t('common.unlike')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                : `${t('common.like')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                " @click.stop="
                                    changeLikeStateForItem($event, item)
                                    ">
                                <svg v-if="!item.has_like" xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                    viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                        stroke-linejoin="round" fill="none" />
                                </svg>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                    viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        fill="#FF5353" />
                                </svg>
                                <span aria-hidden="true">{{
                                    formatLikeCount(item.likes_num)
                                }}</span>
                            </button>
                        </div>
                    </div>

                    <!-- Hover 覆盖层：歌词 + 操作按钮 -->
                    <div class="hover-overlay">
                        <!-- 歌词滚动区域 -->
                        <div class="lyrics-area">
                            <p class="lyrics-text">
                                {{
                                    item.music?.generated_lyrics ||
                                    item.param?.lyrics ||
                                    t('inspiration.noLyrics')
                                }}
                            </p>
                        </div>
                        <!-- 操作按钮行 -->
                        <div class="action-row">
                            <button type="button" class="play-btn" :aria-label="musicPlayer.isCurrentLoading(
                                getInspirationId(item)
                            )
                                ? t('common.loading')
                                : musicPlayer.isCurrentPlaying(
                                    getInspirationId(item)
                                )
                                    ? `${t('common.pause')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                    : `${t('common.play')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                " @click.stop="togglePlay(item)">
                                <!-- 加载中图标 -->
                                <svg v-if="
                                    musicPlayer.isCurrentLoading(
                                        getInspirationId(item)
                                    )
                                " class="animate-spin" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" stroke="white" stroke-width="2"
                                        stroke-opacity="0.3" />
                                    <path d="M12 2a10 10 0 0 1 10 10" stroke="white" stroke-width="2"
                                        stroke-linecap="round" />
                                </svg>
                                <!-- 播放图标 -->
                                <svg v-else-if="
                                    !musicPlayer.isCurrentPlaying(
                                        getInspirationId(item)
                                    )
                                " xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="white" aria-hidden="true">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                                <!-- 暂停图标 -->
                                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 24 24" fill="white" aria-hidden="true">
                                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                </svg>
                            </button>
                            <button type="button" class="make-similar-btn"
                                :aria-label="`${t('menus.aiMusicPage.makeSimilar')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`"
                                @click.stop="handleMusicMakeSimilar(item)">
                                <img src="~/assets/images/common/left-music-icon.png" alt="music" aria-hidden="true"
                                    class="w-[14px] h-[14px]" width="14" height="14" />
                                <span aria-hidden="true">{{ t('menus.aiMusicPage.makeSimilar') }}</span>
                            </button>
                            <button type="button" class="hover-like-btn" :aria-label="item.has_like
                                ? `${t('common.unlike')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                : `${t('common.like')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                " @click.stop="changeLikeStateForItem($event, item)">
                                <svg v-if="!item.has_like" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                                        fill="none" />
                                </svg>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        fill="#FF5353" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 空数据时显示 -->
            <div v-else class="empty-state">
                <img class="w-full max-w-[300px] h-auto aspect-[300/208] object-contain"
                    src="https://static.weryai.com/growth/image/48b2bf9f0fcba0cdd7c00550a00b978b.webp"
                    alt="empty-logo" />
                <div class="text-base text-text-adad mt-5">
                    {{ t('inspiration.noInspirationData') }}
                </div>
            </div>

            <!-- 底部加载更多状态 -->
            <div v-if="loading && !initLoading && tempData.length > 0" class="loading-indicator">
                <l-dot-spinner v-if="isClient" size="30" speed="0.9"
                    color="var(--el-menu-active-color)"></l-dot-spinner>
            </div>

            <!-- 没有更多数据提示 -->
            <div v-if="!hasMore && tempData.length > 0 && !loading" class="no-more-indicator">
                {{ t('inspiration.noMoreData') }}
            </div>
        </template>

        <!-- ==================== 图片/视频类型：瀑布流布局 ==================== -->
        <template v-else>
            <!-- 瀑布流容器 - 有数据时显示 -->
            <div v-if="total_inspiration_data > 0" class="waterfall-container" ref="containerRef"
                :style="{ height: `${containerHeight}px` }">
                <!-- 瀑布流元素 -->
                <div v-for="item in waterfall.items" :key="getInspirationId(item)" class="waterfall-item"
                    :style="item.style" @click="openMask(item)"
                    @mouseenter="(e) => { hoveredItemId = getInspirationId(item); playVideoForItem(e as any, item) }"
                    @mouseleave="(e) => { hoveredItemId = null; pauseVideoForItem(e as any, item) }">
                    <!-- 视频类型 -->
                    <video v-if="
                        getInspirationType(item.type) === 'VIDEO' ||
                        getInspirationOutputType(item.output) === 'VIDEO'
                    " :src="item.output" class="media-content" :poster="getResourceThumbnail(item.output)" muted loop
                        playsinline preload="metadata" @loadedmetadata="onMediaLoad($event, item)"
                        disablepictureinpicture @contextmenu.prevent />

                    <!-- 图片类型 -->
                    <img v-else-if="
                        getInspirationType(item.type) == 'IMAGE' ||
                        getInspirationOutputType(item.output) === 'IMAGE'
                    " :src="item.output" class="media-content" :alt="item.user?.user_name
                        ? `${item.user.user_name}'s AI generated image`
                        : 'AI generated image'
                        " @load="onMediaLoad($event, item)" />

                    <!-- 内容信息层 -->
                    <div class="item-info" :data-id="getInspirationId(item)" @click.stop>
                        <div class="left-info">
                            <div class="user-info">
                                <img v-if="item.user?.avatar" :src="item.user?.avatar" :alt="item.user?.user_name
                                    ? `${item.user.user_name}'s avatar`
                                    : 'User avatar'
                                    " class="avatar" />
                                <img v-else
                                    src="https://static.weryai.com/growth/image/9ead8a3f9b5dd9d44367d3aba6ac8a93.webp"
                                    :alt="item.user?.user_name
                                        ? `${item.user.user_name}'s avatar`
                                        : 'Default user avatar'
                                        " class="avatar" />

                                <span class="username">{{
                                    item.user?.user_name ||
                                    t('inspiration.unknownUser')
                                }}</span>
                            </div>

                            <div class="create" v-show="hoveredItemId === getInspirationId(item) && !isMobile"
                                @click="navigationChange(item)">
                                {{ t('inspiration.generateSimilar') }}
                            </div>
                        </div>
                        <div class="item-stats">
                            <div v-if="!isMobile" class="more-wrapper" :class="{
                                visible:
                                    hoveredItemId === getInspirationId(item),
                            }">
                                <tool-menu-more-function :video-id="getInspirationId(item)" :handleDataItem="item" />
                            </div>

                            <!-- 点赞保持不变 -->
                            <span class="state-item" @click="changeLikeStateForItem($event, item)">
                                {{ item.likes_num || 0 }}
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 20 20"
                                    fill="none" v-show="!item.has_like">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                        stroke-linejoin="round" fill="none" />
                                </svg>
                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 20 20"
                                    fill="none" v-show="item.has_like">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        fill="#FF5353" />
                                </svg>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 空数据时显示 -->
            <div v-else class="empty-state">
                <img class="w-full max-w-[300px] h-auto aspect-[300/208] object-contain"
                    src="https://static.weryai.com/growth/image/48b2bf9f0fcba0cdd7c00550a00b978b.webp"
                    alt="empty-logo" />
                <div class="text-base text-text-adad mt-5">
                    {{ t('inspiration.noInspirationData') }}
                </div>
            </div>

            <!-- 底部加载更多状态 -->
            <div v-if="loading && !initLoading && total_inspiration_data > 0" class="loading-indicator">
                <l-dot-spinner v-if="isClient" size="30" speed="0.9"
                    color="var(--el-menu-active-color)"></l-dot-spinner>
            </div>

            <!-- 没有更多数据提示 -->
            <div v-if="!hasMore && waterfall.items.length > 0 && !loading" class="no-more-indicator">
                {{ t('inspiration.noMoreData') }}
            </div>
        </template>

        <!-- 详情弹窗（音乐/图片/视频共用） -->

        <MobileUgcDetailItemInfo v-if="showMask && isMobile" :showDataItemInfo="currentDetailItem" @closeMask="
            () => {
                showMask = false
            }
        " @changeLikeStateForItem="changeLikeStateForItem" />
        <tool-ugc-detail-item-info v-else-if="showMask" :showDataItemInfo="currentDetailItem" @closeMask="
            () => {
                showMask = false
            }
        " @changeLikeStateForItem="changeLikeStateForItem"></tool-ugc-detail-item-info>
    </div>
</template>

<style scoped lang="less">
.loading-state {
    width: 100%;
    min-height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.empty-state {
    width: 100%;
    height: 100%;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.waterfall-container {
    position: relative;
    width: 100%;
    /* 撑满父容器，通过动态列宽填满 */
    min-height: 400px;
    transition: height 0.3s ease;
    padding: 20px;
    /* 添加内边距 */
    box-sizing: border-box;
}

.waterfall-item {
    border-radius: 8px;
    overflow: hidden;
    background: #333643;
    cursor: pointer;

    .media-content {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    /* 内容信息层（悬浮显示） */
    .item-info {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 12px;
        background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
        color: white;
        opacity: 1;
        transition: opacity 0.2s ease;

        .left-info {
            max-width: 150px;
            overflow: hidden;
            text-overflow: ellipsis;

            .username {
                max-width: 150px;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        .create {
            font-size: 0.75rem;
            background: #99f768;
            padding: 8px 14px;
            border-radius: 8px;
            color: #0d0e12;
            width: fit-content;
            margin-top: 8px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
    }
}

.user-info {
    display: flex;
    align-items: center;
    column-gap: 5px;
    margin-bottom: 3px;

    img.avatar {
        width: 25px;
        height: 25px;
        object-fit: cover;
        border-radius: 50%;
    }

    .username {
        font-size: 14px;
        font-weight: 200;
        white-space: nowrap;
    }
}

.item-stats {
    display: flex;
    gap: 5px;

    .more-wrapper {
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.15s ease;

        &.visible {
            opacity: 1;
            pointer-events: auto;
        }
    }

    .state-item {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        padding: 3px 10px;
        user-select: none;
        background-color: rgba(0, 0, 0, 0.6);
        height: 26px;
        border-radius: 6px;

        &:hover {
            background-color: rgba(13, 14, 18, 0.6);
        }

        i {
            width: 16px;
            height: 16px;
            /* 这里可以使用实际的图标 */
        }
    }
}

/* 响应式调整 */
@media (max-width: 768px) {
    .waterfall-container {
        padding: 8px;
    }
}

/* 加载指示器样式 */
.loading-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px;
}

.no-more-indicator {
    text-align: center;
    padding: 10px;
    color: #8b8d98;
    font-size: 14px;
}

// ==================== 音乐类型卡片样式 ====================

.music-grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, 236px);
    gap: 16px;
    justify-content: start;
}

.music-card {
    width: 236px;
    height: 320px;
    border-radius: 18px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    box-sizing: border-box;
}

// 背景层：图片 + 高斯模糊
.music-card-bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    filter: blur(20px);
    transform: scale(1.1);
    z-index: 0;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.3);
    }
}

// 左上角类型标签
.music-type-tag {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 4px;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 5px;
    height: 22px;
    padding: 0 8px;

    span {
        font-size: 9px;
        color: #fff;
        line-height: 1;
    }
}

// 黑胶唱片
.vinyl-record {
    width: 210px;
    height: 210px;
    position: relative;
    z-index: 1;
    margin-top: 16px;
    flex-shrink: 0;

    &.spinning {
        .vinyl-outer {
            animation: spin 6s linear infinite;
        }

        .vinyl-inner {
            animation: spin-center 6s linear infinite;
        }
    }
}

.vinyl-outer {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: rgba(8, 8, 8, 0.5);
}

.vinyl-inner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 187px;
    height: 187px;
    border-radius: 50%;
    overflow: hidden;
}

.vinyl-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.vinyl-center-dot {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 1);
    border: 2px solid rgba(51, 51, 51, 1);
    box-sizing: border-box;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

@keyframes spin-center {
    from {
        transform: translate(-50%, -50%) rotate(0deg);
    }

    to {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

// 底部区域：常态信息 + 悬浮操作
// 常态信息区
.info-section {
    width: 100%;
    margin-top: auto;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: opacity 0.25s ease;
}

// 歌曲名称行
.song-title-row {
    display: flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
}

.song-title {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 50px); // 预留类型标签空间
}

// 歌曲类型标签
.song-type-tag {
    height: 18px;
    padding: 0 8px;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 20px;
    font-size: 9px;
    color: #fff;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    white-space: nowrap;
}

// 用户信息行
.user-info-row {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.user-left {
    display: flex;
    align-items: center;
    gap: 6px;
    overflow: hidden;
}

.user-avatar {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 1);
    object-fit: cover;
    flex-shrink: 0;
}

.user-name {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100px;
}

.like-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.7);

    span {
        font-size: 12px;
    }

    &:hover {
        opacity: 0.8;
    }
}

// 悬浮：操作按钮覆盖层
// Hover 覆盖层：带高斯模糊背景
.hover-overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    padding: 16px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s ease;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
}

// 歌词滚动区域
.lyrics-area {
    flex: 1;
    overflow-y: auto;
    margin-bottom: 12px;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.lyrics-text {
    font-size: 12px;
    line-height: 30px;
    color: #fff;
    white-space: pre-wrap;
    word-break: break-word;
    text-align: center;
}

// 操作按钮行
.action-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.music-card:hover {
    .info-section {
        opacity: 0;
    }

    .hover-overlay {
        opacity: 1;
        pointer-events: auto;
    }
}

.play-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(167, 171, 178, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(167, 171, 178, 0.5);
    }
}

.hover-like-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(167, 171, 178, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(167, 171, 178, 0.5);
    }
}

.make-similar-btn {
    flex: 1;
    height: 30px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(167, 171, 178, 0.3);
    color: #fff;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    &:hover {
        background: rgba(167, 171, 178, 0.5);
    }
}
</style>
